import React, { useState, useEffect } from "react";
import CardSection from "../components/CardSection";
import { getAnalytics, getPostPerformance, getCalendarSuggestions, getSubscription, api } from "../api";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend, ArcElement,
} from "chart.js";
import { useSubscription } from "../SubscriptionContext";
import UsageBadge from "../components/UsageBadge";
import OnboardingBanner from "../components/OnboardingBanner";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// ── Animated counter ─────────────────────────────────────────────────────────
function useCountUp(target, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) { setCount(0); return; }
    let start = 0;
    const step = Math.ceil(target / (duration / 30));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

// ── Typing effect ────────────────────────────────────────────────────────────
function TypingText({ texts, speed = 80, pause = 2000 }) {
  const [displayed, setDisplayed] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);

  // JS-based cursor blink (no CSS animation)
  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible(v => !v);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const current = texts[textIndex];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplayed(current.substring(0, charIndex + 1));
        if (charIndex + 1 === current.length) {
          setTimeout(() => setDeleting(true), pause);
        } else {
          setCharIndex(charIndex + 1);
        }
      } else {
        setDisplayed(current.substring(0, charIndex - 1));
        if (charIndex - 1 === 0) {
          setDeleting(false);
          setTextIndex((textIndex + 1) % texts.length);
          setCharIndex(0);
        } else {
          setCharIndex(charIndex - 1);
        }
      }
    }, deleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, textIndex, texts, speed, pause]);

  return <span>{displayed}<span style={{ color: '#22d3ee', opacity: cursorVisible ? 1 : 0 }}>|</span></span>;
}

// ── Stat card ────────────────────────────────────────────────────────────────
function AnimatedStatCard({ value, label, icon, color, sublabel }) {
  const animatedValue = useCountUp(value);
  return <StatCard value={animatedValue} label={label} icon={icon} color={color} sublabel={sublabel} />;
}

function StatCard({ value, label, color, sublabel }) {
  const colors = {
    blue:   { border: "border-cyan-500/20", hoverBorder: "hover:border-cyan-500/40", glow: "hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]", text: "text-cyan-400", bar: "bg-gradient-to-r from-cyan-500/60 to-cyan-400/30" },
    purple: { border: "border-purple-500/20", hoverBorder: "hover:border-purple-500/40", glow: "hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]", text: "text-purple-400", bar: "bg-gradient-to-r from-purple-500/60 to-purple-400/30" },
    green:  { border: "border-emerald-500/20", hoverBorder: "hover:border-emerald-500/40", glow: "hover:shadow-[0_0_30px_rgba(16,185,129,0.15)]", text: "text-emerald-400", bar: "bg-gradient-to-r from-emerald-500/60 to-emerald-400/30" },
    amber:  { border: "border-amber-500/20", hoverBorder: "hover:border-amber-500/40", glow: "hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]", text: "text-amber-400", bar: "bg-gradient-to-r from-amber-500/60 to-amber-400/30" },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-[#0d1117]/80 ${c.border} ${c.hoverBorder} ${c.glow} border transition-all duration-500 p-5 group cursor-default`}
      style={{ backdropFilter: "blur(10px)" }}>
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        <p className={`text-4xl font-black tabular-nums ${c.text} transition-all duration-300 group-hover:scale-105 origin-left`}>{value}</p>
        <p className="text-xs uppercase tracking-widest text-gray-300 font-semibold mt-2">{label}</p>
        {sublabel && <p className="text-[11px] text-gray-500 mt-1">{sublabel}</p>}
      </div>

      {/* Bottom glow bar */}
      <div className={`absolute bottom-0 left-0 h-[2px] w-full ${c.bar} opacity-40 group-hover:opacity-80 transition-opacity duration-500`} />
    </div>
  );
}

// ── Posting time suggestion card ─────────────────────────────────────────────
function SuggestionCard({ day, time, reason, index }) {
  const colors = [
    { border: "border-cyan-500/20", hoverBorder: "hover:border-cyan-500/40", glow: "hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]", text: "text-cyan-400", bar: "bg-gradient-to-r from-cyan-500/60 to-cyan-400/30" },
    { border: "border-amber-500/20", hoverBorder: "hover:border-amber-500/40", glow: "hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]", text: "text-amber-400", bar: "bg-gradient-to-r from-amber-500/60 to-amber-400/30" },
    { border: "border-emerald-500/20", hoverBorder: "hover:border-emerald-500/40", glow: "hover:shadow-[0_0_30px_rgba(16,185,129,0.15)]", text: "text-emerald-400", bar: "bg-gradient-to-r from-emerald-500/60 to-emerald-400/30" },
    { border: "border-purple-500/20", hoverBorder: "hover:border-purple-500/40", glow: "hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]", text: "text-purple-400", bar: "bg-gradient-to-r from-purple-500/60 to-purple-400/30" },
  ];
  const c = colors[index % colors.length];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-[#0d1117]/80 ${c.border} ${c.hoverBorder} ${c.glow} border transition-all duration-500 p-5 group cursor-default`}
      style={{ backdropFilter: "blur(10px)" }}>
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative z-10 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-bold text-sm">{day}</p>
            <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${c.text} bg-white/5`}>
              {time}
            </span>
          </div>
          <p className="text-gray-500 text-xs mt-1">{reason}</p>
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 h-[2px] w-full ${c.bar} opacity-40 group-hover:opacity-80 transition-opacity duration-500`} />
    </div>
  );
}

// ── Status dot - static to prevent flicker ───────────────────────────────────
function BlippingDot({ color = "#25D366" }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: color, boxShadow: `0 0 8px ${color}` }}></span>
    </span>
  );
}

// ── WhatsApp AI Bot Card ──────────────────────────────────────────────────────
function WhatsAppCard() {
  const [serverStatus, setServerStatus] = useState(null);
  const [phone, setPhone]       = useState("");
  const [linked, setLinked]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    api.get("/whatsapp/status").then(r => setServerStatus(r.data)).catch(() => setServerStatus({ configured: false }));
  }, []);

  const linkNumber = async () => {
    if (!phone.trim()) return;
    setSaving(true); setFeedback(null);
    try {
      await api.post("/whatsapp/connect", { phone: phone.trim() });
      setLinked(true);
      setFeedback({ ok: true, text: "Linked! Check your WhatsApp for a welcome message." });
    } catch(e) {
      setFeedback({ ok: false, text: e?.response?.data?.error || "Failed to link number." });
    } finally { setSaving(false); }
  };

  const unlinkNumber = async () => {
    await api.post("/whatsapp/disconnect").catch(() => {});
    setLinked(false); setPhone(""); setFeedback({ ok: true, text: "Disconnected." });
  };

  const isConfigured = serverStatus?.configured;
  const WA_GREEN = "#25D366";

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: "linear-gradient(180deg, rgba(15,15,20,0.95), rgba(10,10,14,0.98))", border: "1px solid rgba(37,211,102,0.2)", boxShadow: "0 0 60px rgba(37,211,102,0.06), 0 0 30px rgba(0,0,0,0.5)" }}>

      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between gap-3"
        style={{ background: "linear-gradient(135deg, rgba(37,211,102,0.12), rgba(37,211,102,0.04))", borderBottom: "1px solid rgba(37,211,102,0.15)" }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(37,211,102,0.3)", boxShadow: "0 0 20px rgba(37,211,102,0.15)" }}>
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill={WA_GREEN}/>
              <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.954-1.418A9.953 9.953 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.946 7.946 0 01-4.079-1.123l-.292-.173-3.03.868.87-3.027-.19-.31A7.957 7.957 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" fill={WA_GREEN}/>
            </svg>
          </div>
          <div>
            <p className="text-base font-black text-white tracking-tight">WhatsApp AI Bot</p>
            <p className="text-[11px] text-green-400/80">Control Kronos from WhatsApp</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          style={{ background: isConfigured ? "rgba(37,211,102,0.15)" : "rgba(245,158,11,0.15)", border: `1px solid ${isConfigured ? "rgba(37,211,102,0.3)" : "rgba(245,158,11,0.3)"}` }}>
          {isConfigured && <BlippingDot color={WA_GREEN} />}
          <span className="text-xs font-bold" style={{ color: isConfigured ? WA_GREEN : "#f59e0b" }}>
            {serverStatus === null ? "..." : isConfigured ? "API Ready" : "Not Configured"}
          </span>
        </div>
      </div>

      {/* Explanation Banner */}
      <div className="px-5 py-3" style={{ background: "rgba(37,211,102,0.04)", borderBottom: "1px solid rgba(37,211,102,0.08)" }}>
        <p className="text-xs text-gray-400 leading-relaxed">
          <span className="text-green-400 font-semibold">What can you do?</span> Send commands via WhatsApp to
          <span className="text-cyan-400"> post to social media</span>,
          <span className="text-purple-400"> generate proposals</span>,
          <span className="text-amber-400"> create documents</span>, and more.
        </p>
      </div>

      {/* Features Grid */}
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {[
            { icon: "📱", text: "Post to social", color: "#22d3ee" },
            { icon: "📝", text: "Generate proposals", color: "#a78bfa" },
            { icon: "📄", text: "Create documents", color: "#f59e0b" },
            { icon: "📊", text: "Get your stats", color: "#34d399" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all hover:scale-[1.02]"
              style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <span className="text-base">{f.icon}</span>
              <span className="text-xs font-medium" style={{ color: f.color }}>{f.text}</span>
            </div>
          ))}
        </div>

        {/* Link number */}
        {!linked ? (
          <div className="space-y-3 pt-2">
            <p className="text-xs text-gray-500">Link your WhatsApp number to activate the bot:</p>
            <div className="flex gap-2">
              <input value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+923001234567"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-green-500/30"
                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}
                onFocus={(e) => e.target.style.borderColor = "rgba(37,211,102,0.4)"}
                onBlur={(e) => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
              <button onClick={linkNumber} disabled={saving || !phone.trim()}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg, rgba(37,211,102,0.5), rgba(37,211,102,0.3))", border: "1px solid rgba(37,211,102,0.5)", boxShadow: "0 0 20px rgba(37,211,102,0.15)" }}>
                {saving ? "..." : "Link"}
              </button>
            </div>
            {feedback && <p className={`text-xs ${feedback.ok ? "text-green-400" : "text-red-400"}`}>{feedback.text}</p>}
            <p className="text-[10px] text-gray-600">Professional+ plan required for AI commands</p>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: "rgba(37,211,102,0.08)", border: "1px solid rgba(37,211,102,0.2)", boxShadow: "0 0 20px rgba(37,211,102,0.08)" }}>
              <BlippingDot color={WA_GREEN} />
              <span className="text-sm text-green-400 font-semibold">Bot active on {phone}</span>
            </div>
            <p className="text-xs text-gray-500">Send <strong className="text-white">HELP</strong> to your WhatsApp to see all commands.</p>
            <button onClick={unlinkNumber} className="text-xs text-red-400 hover:text-red-300 transition-colors">Disconnect</button>
            {feedback && <p className={`text-xs ${feedback.ok ? "text-green-400" : "text-red-400"}`}>{feedback.text}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Feature quick-access ─────────────────────────────────────────────────────
function QuickAction({ label, desc, color, onClick }) {
  const colors = {
    blue:   { border: "rgba(6,182,212,0.25)", hoverBorder: "rgba(6,182,212,0.5)", glow: "0 0 30px rgba(6,182,212,0.15)", text: "text-cyan-400" },
    purple: { border: "rgba(168,85,247,0.25)", hoverBorder: "rgba(168,85,247,0.5)", glow: "0 0 30px rgba(168,85,247,0.15)", text: "text-purple-400" },
    green:  { border: "rgba(16,185,129,0.25)", hoverBorder: "rgba(16,185,129,0.5)", glow: "0 0 30px rgba(16,185,129,0.15)", text: "text-emerald-400" },
    orange: { border: "rgba(249,115,22,0.25)", hoverBorder: "rgba(249,115,22,0.5)", glow: "0 0 30px rgba(249,115,22,0.15)", text: "text-orange-400" },
    amber:  { border: "rgba(245,158,11,0.25)", hoverBorder: "rgba(245,158,11,0.5)", glow: "0 0 30px rgba(245,158,11,0.15)", text: "text-amber-400" },
  };
  const c = colors[color] || colors.blue;
  return (
    <button
      onClick={onClick}
      className="relative overflow-hidden flex items-center gap-4 rounded-2xl text-left w-full group cursor-pointer transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: "#000000",
        border: `1px solid ${c.border}`,
        padding: "20px 24px",
        minHeight: "80px"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = c.hoverBorder;
        e.currentTarget.style.boxShadow = c.glow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = c.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div className="relative z-10 flex-1 min-w-0">
        <p className={`text-base font-semibold ${c.text} group-hover:text-white transition-colors`}>{label}</p>
        <p className="text-gray-500 text-sm mt-1">{desc}</p>
      </div>
      <span className={`relative z-10 ${c.text} opacity-50 group-hover:opacity-100 transition-all text-lg group-hover:translate-x-1`}>→</span>
    </button>
  );
}

// ── Webhooks / Zapier integration card ───────────────────────────────────────
function WebhooksCard() {
  const [hooks, setHooks]       = useState([]);
  const [url, setUrl]           = useState("");
  const [label, setLabel]       = useState("");
  const [events, setEvents]     = useState(["proposal.generated"]);
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  const EVENT_OPTIONS = [
    { value: "proposal.generated", label: "Proposal Generated" },
    { value: "post.published",     label: "Post Published" },
    { value: "call.received",      label: "Call Received" },
    { value: "subscription.updated", label: "Subscription Updated" },
  ];

  const loadHooks = () => api.get("/integrations/webhooks").then(r => setHooks(r.data || [])).catch(() => {});
  useEffect(() => { loadHooks(); }, []);

  const toggleEvent = (v) => setEvents(prev => prev.includes(v) ? prev.filter(e => e !== v) : [...prev, v]);

  const saveHook = async () => {
    if (!url.trim() || !events.length) return;
    setSaving(true);
    try {
      await api.post("/integrations/webhooks", { url: url.trim(), events, label });
      setUrl(""); setLabel(""); setEvents(["proposal.generated"]);
      setFeedback({ ok: true, text: "Webhook saved!" });
      loadHooks();
    } catch { setFeedback({ ok: false, text: "Failed to save." }); }
    finally { setSaving(false); setTimeout(() => setFeedback(null), 3000); }
  };

  const deleteHook = async (id) => {
    await api.delete("/integrations/webhooks/" + id).catch(() => {});
    loadHooks();
  };

  const testHook = async (id) => {
    const r = await api.post("/integrations/webhooks/" + id + "/test").catch(() => ({ data: { ok: false } }));
    setFeedback({ ok: r.data.ok, text: r.data.ok ? "Test sent!" : "Test failed: " + (r.data.error || "error") });
    setTimeout(() => setFeedback(null), 4000);
  };

  const INDIGO = "#6366f1";

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: "linear-gradient(180deg, rgba(15,15,20,0.95), rgba(10,10,14,0.98))", border: "1px solid rgba(99,102,241,0.2)", boxShadow: "0 0 60px rgba(99,102,241,0.06), 0 0 30px rgba(0,0,0,0.5)" }}>

      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between gap-3"
        style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(99,102,241,0.04))", borderBottom: "1px solid rgba(99,102,241,0.15)" }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(99,102,241,0.3)", boxShadow: "0 0 20px rgba(99,102,241,0.15)" }}>
            <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
          </div>
          <div>
            <p className="text-base font-black text-white tracking-tight">Webhooks / Zapier</p>
            <p className="text-[11px] text-indigo-400/80">Automate with any external service</p>
          </div>
        </div>
        {hooks.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)" }}>
            <BlippingDot color={INDIGO} />
            <span className="text-xs font-bold text-indigo-400">{hooks.length} Active</span>
          </div>
        )}
      </div>

      {/* Explanation Banner */}
      <div className="px-5 py-3" style={{ background: "rgba(99,102,241,0.04)", borderBottom: "1px solid rgba(99,102,241,0.08)" }}>
        <p className="text-xs text-gray-400 leading-relaxed">
          <span className="text-indigo-400 font-semibold">What is this?</span> Connect Kronos to
          <span className="text-orange-400"> Zapier</span>,
          <span className="text-cyan-400"> Make.com</span>, or any webhook URL.
          <span className="text-emerald-400 font-medium"> Automate your workflow</span> when proposals are generated or posts are published.
        </p>
      </div>

      <div className="p-5 space-y-4">
        {/* Add webhook */}
        <div className="space-y-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Add New Webhook</p>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://hooks.zapier.com/..."
            className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}
            onFocus={(e) => e.target.style.borderColor = "rgba(99,102,241,0.4)"}
            onBlur={(e) => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (optional)"
            className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}
            onFocus={(e) => e.target.style.borderColor = "rgba(99,102,241,0.4)"}
            onBlur={(e) => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
          <div className="flex flex-wrap gap-2">
            {EVENT_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => toggleEvent(opt.value)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105"
                style={{
                  background: events.includes(opt.value) ? "rgba(99,102,241,0.25)" : "rgba(0,0,0,0.3)",
                  border: `1px solid ${events.includes(opt.value) ? "rgba(99,102,241,0.4)" : "rgba(255,255,255,0.08)"}`,
                  color: events.includes(opt.value) ? "#a5b4fc" : "#6b7280",
                  boxShadow: events.includes(opt.value) ? "0 0 15px rgba(99,102,241,0.15)" : "none"
                }}>
                {opt.label}
              </button>
            ))}
          </div>
          <button onClick={saveHook} disabled={saving || !url.trim()}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40 hover:scale-[1.02]"
            style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.5), rgba(99,102,241,0.3))", border: "1px solid rgba(99,102,241,0.5)", boxShadow: "0 0 20px rgba(99,102,241,0.15)" }}>
            {saving ? "Saving..." : "+ Add Webhook"}
          </button>
          {feedback && <p className={"text-xs text-center " + (feedback.ok ? "text-emerald-400" : "text-red-400")}>{feedback.text}</p>}
        </div>

        {/* Existing hooks */}
        {hooks.length > 0 && (
          <div className="space-y-2 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold pt-2">Active Webhooks</p>
            {hooks.map(h => (
              <div key={h.id} className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.01]"
                style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(99,102,241,0.15)" }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(99,102,241,0.15)" }}>
                  <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white font-semibold truncate">{h.label || "Webhook"}</p>
                  <p className="text-[10px] text-gray-500 truncate">{h.url}</p>
                </div>
                <button onClick={() => testHook(h.id)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-indigo-400 transition-all hover:scale-105"
                  style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)" }}>
                  Test
                </button>
                <button onClick={() => deleteHook(h.id)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-red-400 transition-all hover:scale-105"
                  style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
                  Del
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardTab({ onCtaClick }) {
  const { subscription: subCtx } = useSubscription();
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const isLoggedIn = !!localStorage.getItem("token");

  useEffect(() => {
    const defaults = { scheduledPosts: 0, postedPosts: 0, proposalsGenerated: 0, jobsFound: 0 };
    if (!isLoggedIn) {
      setAnalytics(defaults);
      setSuggestions([
        { day: "Monday",    time: "10:00", reason: "High engagement — start of work week" },
        { day: "Wednesday", time: "14:00", reason: "Mid-week peak — audiences most active" },
        { day: "Friday",    time: "18:00", reason: "Weekend anticipation — shares spike" },
        { day: "Sunday",    time: "20:00", reason: "Relaxed browsing — great for lifestyle content" },
      ]);
      setSubscription({ tier: "free", price: 0 });
      return;
    }
    Promise.allSettled([
      getAnalytics(), getPostPerformance(),
      getCalendarSuggestions(), getSubscription(),
    ]).then(([a, p, s, sub]) => {
      setAnalytics(a.status === "fulfilled" ? a.value.data : defaults);
      setPerformance(p.status === "fulfilled" ? (p.value.data.items || p.value.data || []) : []);
      setSuggestions(s.status === "fulfilled" ? s.value.data : []);
      setSubscription(sub.status === "fulfilled" ? sub.value.data : { tier: "free", price: 0 });
    });
  }, [isLoggedIn]);

  const totalLikes    = performance.reduce((s, p) => s + (p.likes    || 0), 0);
  const totalShares   = performance.reduce((s, p) => s + (p.shares   || 0), 0);
  const totalComments = performance.reduce((s, p) => s + (p.comments || 0), 0);
  const totalViews    = performance.reduce((s, p) => s + (p.views    || 0), 0);
  const totalEngagement = totalLikes + totalShares + totalComments;

  const tier = subscription?.tier || "free";
  const tierColors = {
    free:         "text-gray-400",
    professional: "text-cyan-400",
    business:     "text-purple-400",
    enterprise:   "text-amber-400",
  };

  return (
    <div className="space-y-8">
      {isLoggedIn && <OnboardingBanner onNavigate={onCtaClick} />}

      {/* ── Hero — Dark AMOLED Glowy Style ── */}
      <div className="relative overflow-hidden rounded-2xl px-6 py-8 sm:py-10 text-center"
        style={{
          background: "linear-gradient(180deg, rgba(5,5,8,0.98), rgba(8,8,12,0.95))",
          border: "1px solid rgba(100,180,255,0.15)",
          boxShadow: "0 0 80px rgba(100,180,255,0.06), 0 0 40px rgba(139,92,246,0.04), inset 0 1px 0 rgba(255,255,255,0.03)"
        }}>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(100,180,255,0.1),transparent)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-cyan-500/60 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

        {/* Current Plan Badge - Top Right */}
        {isLoggedIn && subscription && (
          <div className="absolute top-4 right-4 z-10">
            <div className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${
              tier === 'enterprise' ? 'text-amber-400' :
              tier === 'business' ? 'text-purple-400' :
              tier === 'professional' ? 'text-cyan-400' :
              'text-gray-400'
            }`} style={{
              background: tier === 'enterprise' ? 'rgba(245,158,11,0.1)' :
                         tier === 'business' ? 'rgba(168,85,247,0.1)' :
                         tier === 'professional' ? 'rgba(34,211,238,0.1)' : 'rgba(107,114,128,0.1)',
              border: `1px solid ${tier === 'enterprise' ? 'rgba(245,158,11,0.25)' :
                                   tier === 'business' ? 'rgba(168,85,247,0.25)' :
                                   tier === 'professional' ? 'rgba(34,211,238,0.25)' : 'rgba(107,114,128,0.25)'}`,
              boxShadow: `0 0 20px ${tier === 'enterprise' ? 'rgba(245,158,11,0.1)' :
                                     tier === 'business' ? 'rgba(168,85,247,0.1)' :
                                     tier === 'professional' ? 'rgba(34,211,238,0.1)' : 'rgba(107,114,128,0.1)'}`
            }}>
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  tier === 'enterprise' ? 'bg-amber-400' :
                  tier === 'business' ? 'bg-purple-400' :
                  tier === 'professional' ? 'bg-cyan-400' : 'bg-gray-400'
                }`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${
                  tier === 'enterprise' ? 'bg-amber-400' :
                  tier === 'business' ? 'bg-purple-400' :
                  tier === 'professional' ? 'bg-cyan-400' : 'bg-gray-400'
                }`}></span>
              </span>
              {tier} Plan
            </div>
          </div>
        )}

        <div className="relative">
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-xl mb-5 uppercase tracking-wider text-xs font-bold"
            style={{
              background: "linear-gradient(135deg, rgba(34,211,238,0.12), rgba(34,211,238,0.06))",
              border: "1px solid rgba(34,211,238,0.25)",
              boxShadow: "0 0 25px rgba(34,211,238,0.12), inset 0 0 15px rgba(34,211,238,0.05)",
              color: "#22d3ee"
            }}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400 shadow-lg shadow-cyan-400/50"></span>
            </span>
            AI Command Center
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">
            <span className="text-white">
              Welcome to{" "}
            </span>
            <span style={{
              background: "linear-gradient(135deg, #22d3ee, #06b6d4, #0891b2)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0 0 40px rgba(34,211,238,0.3)"
            }}>
              Kronos AI
            </span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed min-h-[28px]">
            <TypingText texts={[
              "Automate your phone calls with AI receptionist...",
              "Generate winning proposals in seconds...",
              "Create & schedule social media content...",
              "Scale your business with AI automation..."
            ]} />
          </p>
        </div>
      </div>

      {/* ── Social Proof ── */}
      <div className="relative overflow-hidden flex flex-col items-center gap-3 sm:gap-4 py-4 sm:py-5 rounded-2xl border border-white/[0.06] bg-[#0d1117]/60 hover:border-white/10 transition-all duration-500"
        style={{ backdropFilter: "blur(10px)" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.01] to-transparent" />
        <p className="relative z-10 text-xs sm:text-sm text-gray-500 uppercase tracking-widest font-semibold">Powered by Industry Leaders</p>
        <div className="relative z-10 flex items-center gap-4 sm:gap-8 flex-wrap justify-center px-4">
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Google Gemini</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12 0C5.372 0 0 5.373 0 12s5.372 12 12 12 12-5.373 12-12S18.628 0 12 0zm-.5 3.5h1v3.25l2.813 1.625-.5.866L12 7.25V3.5zm-5.75 5h3.5v1h-3.5v-1zm0 6h3.5v1h-3.5v-1zm11.5-3h-3.5v-1h3.5v1z" fill="#4285F4"/><path d="M19.35 10.04A7.49 7.49 0 0012 4.5a7.49 7.49 0 00-7.35 5.54A4.5 4.5 0 005 19.5h13.5a3.75 3.75 0 00.85-7.46z" fill="#4285F4"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Google Cloud</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M13.23 10.56V10c0-1.94 1.4-3.42 3.38-3.42 1.97 0 3.41 1.48 3.41 3.42v.56h1.49V10c0-2.8-2.05-4.9-4.9-4.9-2.83 0-4.87 2.1-4.87 4.9v.56h1.49z" fill="#FF9900"/><path d="M6.5 10.56V10c0-2.8 2.04-4.9 4.87-4.9v1.48c-1.97 0-3.38 1.48-3.38 3.42v.56H6.5z" fill="#FF9900"/><path d="M3.58 13.72v2.97c0 2.18 1.77 3.95 3.95 3.95h8.94c2.18 0 3.95-1.77 3.95-3.95v-2.97H3.58z" fill="#FF9900"/><rect x="3.58" y="10.56" width="16.84" height="3.16" rx="1" fill="#FF9900"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">AWS Bedrock</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#00D4AA" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">BytePlus AI</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm3.248 18.348l-.003-.01-1.399-4.166-4.727 1.456 1.399 4.17.003.007a8.49 8.49 0 01-4.033-2.07l3.032-3.697-3.452-3.296-3.032 3.697A8.49 8.49 0 013.51 9.99l4.727-1.456L6.84 4.364A8.49 8.49 0 0112 3.51c1.48 0 2.88.38 4.096 1.046L12.16 8.07l3.453 3.297 3.937-4.514a8.49 8.49 0 011.94 4.448l-4.728 1.456 1.399 4.17a8.49 8.49 0 01-2.913 1.421z" fill="#F22F46"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Twilio</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#2C2C2C" stroke="#5B5FC7" strokeWidth="1.5"/><path d="M8 9.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5v-5z" fill="#5B5FC7"/><path d="M10.5 11.5l1.5 1.5 2-2" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Retell AI</span>
          </span>
        </div>
      </div>

      {/* ── AI Engines Badge Row - Mobile Responsive ── */}
      <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold text-center mb-2">Primary AI Models</p>
      <div className="flex justify-center px-4">
        <div className="grid grid-cols-2 sm:inline-flex sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-2.5 rounded-2xl border border-white/[0.06] bg-[#0d1117]/60 hover:border-white/10 transition-all duration-500 w-full sm:w-auto"
          style={{ backdropFilter: "blur(10px)" }}>
          {/* Google Gemini */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">Gemini 3.1 Pro</span>
          </span>
          <span className="hidden sm:inline text-gray-700">|</span>
          {/* Claude */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#D97757"/><path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="#D97757"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">Claude Sonnet</span>
          </span>
          <span className="hidden sm:inline text-gray-700">|</span>
          {/* ByteDance */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#00D4AA" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">ByteDance</span>
          </span>
          <span className="hidden sm:inline text-gray-700">|</span>
          {/* Dreamina */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><defs><linearGradient id="dreamina-grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FF6B9D"/><stop offset="100%" stopColor="#C44DFF"/></linearGradient></defs><circle cx="12" cy="12" r="10" fill="url(#dreamina-grad)"/><path d="M8 12c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><circle cx="12" cy="12" r="1.5" fill="white"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">Dreamina</span>
          </span>
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div>
        <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Quick Access</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <QuickAction label="Social Media" desc="Generate & schedule posts" color="blue" onClick={() => onCtaClick?.("social-media")} />
          <QuickAction label="AI Receptionist" desc="Never miss a call" color="purple" onClick={() => onCtaClick?.("ai-receptionist")} />
          <QuickAction label="Proposals" desc="Find jobs & win clients" color="green" onClick={() => onCtaClick?.("proposals")} />
          <QuickAction label="Doc Studio" desc="Create documents & strategies" color="orange" onClick={() => onCtaClick?.("doc-studio")} />
          <QuickAction label="Upgrade Plan" desc={tier === "enterprise" ? "You're on the top plan" : "Unlock more features"} color="amber" onClick={() => onCtaClick?.("pricing")} />
        </div>
      </div>

      {/* ── Stats row ── */}
      {analytics && (
        <div>
          <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Activity Overview</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <AnimatedStatCard value={analytics.totalAiCalls || 0}       label="Total AI Calls"        color="blue"   sublabel="All AI generations" />
            <AnimatedStatCard value={analytics.proposalsGenerated || 0} label="Proposals Generated"   color="green"  sublabel="AI-written proposals" />
            <AnimatedStatCard value={analytics.docsGenerated || 0}      label="Documents Created"     color="purple" sublabel="Doc Studio outputs" />
            <AnimatedStatCard value={analytics.socialGenerated || 0}    label="Social Content"        color="amber"  sublabel="Posts & captions" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3">
            <StatCard value={analytics.scheduledPosts || 0}    label="Scheduled Posts"      color="blue"   sublabel="Queued for publishing" />
            <StatCard value={analytics.postedPosts || 0}       label="Published Posts"       color="purple" sublabel="Successfully posted" />
            <StatCard value={analytics.connectedAccounts || 0} label="Connected Accounts"   color="green"  sublabel="Social platforms" />
            <StatCard value={analytics.jobsFound || 0}         label="Jobs Found"            color="amber"  sublabel="Active opportunities" />
          </div>
        </div>
      )}

      {/* ── Subscription + Engagement side by side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Subscription panel — 2/3 width */}
        {subscription && (
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-cyan-500/20 hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)] transition-all duration-500 p-5 space-y-4"
            style={{ backdropFilter: "blur(10px)" }}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <div className="relative z-10 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <p className={`text-base font-bold capitalize ${tierColors[tier] || "text-white"}`}>
                    {tier} Plan
                  </p>
                  <p className="text-gray-500 text-xs">${subscription.price}/month</p>
                </div>
              </div>
              {tier !== "enterprise" && (
                <button
                  onClick={() => onCtaClick?.("pricing")}
                  className="px-4 py-2 rounded-xl bg-white/5 border border-cyan-500/30 text-cyan-400 text-xs font-bold hover:bg-cyan-500/10 hover:border-cyan-500/50 transition-all">
                  Upgrade Plan →
                </button>
              )}
            </div>
            {subCtx?.usage ? (
              <div className="relative z-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <UsageBadge label="Posts"     used={subCtx.usage.posts.used}           limit={subCtx.usage.posts.limit} />
                <UsageBadge label="Images"    used={subCtx.usage.images.used}          limit={subCtx.usage.images.limit} />
                <UsageBadge label="Videos"    used={subCtx.usage.videos.used}          limit={subCtx.usage.videos.limit} />
                <UsageBadge label="Proposals" used={subCtx.usage.proposals.used}       limit={subCtx.usage.proposals.limit} />
                <UsageBadge label="Accounts"  used={subCtx.usage.social_accounts.used} limit={subCtx.usage.social_accounts.limit} />
                <UsageBadge label="Scheduled" used={subCtx.usage.scheduled_posts.used} limit={subCtx.usage.scheduled_posts.limit} />
              </div>
            ) : (
              <p className="text-gray-600 text-sm">Log in to see your usage</p>
            )}
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-cyan-500/60 to-cyan-400/30 opacity-40" />
          </div>
        )}

        {/* Engagement donut — 1/3 width */}
        {performance.length > 0 && totalEngagement > 0 ? (
          <div className="relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-purple-500/20 hover:border-purple-500/40 hover:shadow-[0_0_30px_rgba(168,85,247,0.1)] transition-all duration-500 p-5 flex flex-col items-center justify-center gap-3"
            style={{ backdropFilter: "blur(10px)" }}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <p className="relative z-10 text-xs text-gray-500 uppercase tracking-widest font-semibold">Total Engagement</p>
            <div className="relative z-10 w-32 h-32">
              <Doughnut
                data={{
                  labels: ["Likes", "Shares", "Comments"],
                  datasets: [{
                    data: [totalLikes, totalShares, totalComments],
                    backgroundColor: ["rgba(6,182,212,0.8)","rgba(168,85,247,0.8)","rgba(16,185,129,0.8)"],
                    borderColor: ["rgb(6,182,212)","rgb(168,85,247)","rgb(16,185,129)"],
                    borderWidth: 2,
                  }]
                }}
                options={{ plugins: { legend: { display: false } }, cutout: "70%" }}
              />
            </div>
            <div className="relative z-10 text-center">
              <p className="text-2xl font-black text-purple-400">{totalEngagement.toLocaleString()}</p>
              <p className="text-xs text-gray-500">interactions</p>
            </div>
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-purple-500/60 to-purple-400/30 opacity-40" />
          </div>
        ) : !performance.length && subscription ? (
          <div className="relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-white/10 border-dashed hover:border-white/20 transition-all duration-500 p-5 flex flex-col items-center justify-center gap-2 text-center"
            style={{ backdropFilter: "blur(10px)" }}>
            <svg className="w-8 h-8 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>
            </svg>
            <p className="text-gray-500 text-sm font-medium">No engagement data yet</p>
            <p className="text-gray-600 text-xs">Post content to see analytics here</p>
            <button onClick={() => onCtaClick?.("social-media")} className="mt-1 text-xs text-cyan-400 hover:text-cyan-300">
              Start posting →
            </button>
          </div>
        ) : null}
      </div>

      {/* ── Bar chart (only when data exists) ── */}
      {performance.length > 0 && (
        <div className="relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-emerald-500/20 hover:border-emerald-500/40 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-500 p-5"
          style={{ backdropFilter: "blur(10px)" }}>
          <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-emerald-500/60 to-emerald-400/30 opacity-40" />
          <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">Post Performance</h2>
          <Bar
            data={{
              labels: ["Likes", "Shares", "Comments", "Views"],
              datasets: [{
                label: "Engagement",
                data: [totalLikes, totalShares, totalComments, totalViews],
                backgroundColor: ["rgba(6,182,212,0.7)","rgba(168,85,247,0.7)","rgba(16,185,129,0.7)","rgba(251,146,60,0.7)"],
                borderColor:      ["rgb(6,182,212)","rgb(168,85,247)","rgb(16,185,129)","rgb(251,146,60)"],
                borderWidth: 2, borderRadius: 8,
              }]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              scales: {
                y: { ticks: { color: "#6b7280" }, grid: { color: "rgba(255,255,255,0.05)" } },
                x: { ticks: { color: "#6b7280" }, grid: { display: false } },
              }
            }}
          />
        </div>
      )}

      {/* ── AI Posting Suggestions ── */}
      {suggestions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold">AI Best Posting Times</h2>
            <span className="text-xs text-gray-600">Based on engagement patterns</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {suggestions.map((s, i) => (
              <SuggestionCard key={i} index={i} day={s.day} time={s.time} reason={s.reason} />
            ))}
          </div>
        </div>
      )}

      {/* ── Integrations ── */}
      <div>
        <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Integrations</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WhatsAppCard />
          <WebhooksCard />
        </div>
      </div>

      {/* ── Feature cards ── */}
      <div>
        <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Platform Features</h2>
        <CardSection
          heading=""
          subtitle=""
          cardData={[
            { heading: "AI Receptionist",       description: <span className="text-sm text-gray-300">24/7 intelligent phone assistant that handles calls, books appointments, and answers customer queries automatically.</span>, cta: "Setup Now", hue: 280, ctaAction: "ai-receptionist" },
            { heading: "Social Media Hub",       description: <span className="text-sm text-gray-300">AI-powered content creation, stunning visuals, and automated scheduling across all your social channels.</span>, cta: "Open Hub", hue: 210, ctaAction: "social-media" },
            { heading: "Proposal Intelligence", description: <span className="text-sm text-gray-300">Find high-value freelance jobs and generate personalized, winning proposals in seconds with AI.</span>, cta: "Find Jobs", hue: 165, ctaAction: "proposals" },
            { heading: "Doc Studio",              description: <span className="text-sm text-gray-300">Craft enterprise-grade documents, business plans, contracts, and strategies with mythical precision using Claude AI.</span>, cta: "Create Now", hue: 25, ctaAction: "canvas" },
            { heading: "Upgrade Your Plan",     description: <span className="text-sm text-gray-300">Unlock unlimited posts, video generation, more social accounts, and priority support.</span>, cta: "View Plans", hue: 338, ctaAction: "pricing" },
          ]}
          onCtaClick={onCtaClick}
        />
      </div>
    </div>
  );
}
